import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import LaunchIcon from '@mui/icons-material/Launch';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  doc, getDoc, collection, onSnapshot, addDoc, deleteDoc,
  serverTimestamp, query, orderBy, Timestamp,
} from 'firebase/firestore';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { usePostProject } from '../hooks/usePostProject';

interface Post {
  title: string;
  content: string;
  authorId: string;
  authorName: string;
  authorPhotoURL?: string;
  createdAt: Timestamp | null;
  published: boolean;
}

interface Comment {
  id: string;
  content: string;
  authorId: string;
  authorName: string;
  authorPhotoURL?: string;
  createdAt: Timestamp | null;
}

type SnackState = { open: boolean; message: string; severity: 'success' | 'error' };

function formatDate(ts: Timestamp | null): string {
  if (!ts) return '';
  return ts.toDate().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

function PostSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Skeleton variant="text" width="70%" height={48} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton variant="circular" width={36} height={36} />
        <Skeleton variant="text" width={160} />
      </Box>
      <Skeleton variant="rectangular" height={200} />
    </Box>
  );
}

interface CommentFormProps {
  postId: string;
  photoURL?: string;
  name: string;
  onError: (msg: string) => void;
}

function CommentForm({ postId, photoURL, name, onError }: CommentFormProps) {
  const { authUser, currentUser } = useAuth();
  const [text, setText] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || !authUser || !currentUser) return;
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'posts', postId, 'comments'), {
        content: trimmed,
        authorId: authUser.uid,
        authorName: currentUser.name,
        ...(currentUser.photoURL && { authorPhotoURL: currentUser.photoURL }),
        createdAt: serverTimestamp(),
      });
      setText('');
    } catch {
      onError('Failed to post comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
      <Avatar src={photoURL} sx={{ width: 32, height: 32, fontSize: '0.75rem', mt: 1 }}>
        {name[0]?.toUpperCase()}
      </Avatar>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            '&:focus-within': {
              borderColor: 'primary.main',
              boxShadow: theme => `0 0 0 1px ${theme.palette.primary.main}`,
            },
          }}
        >
          <textarea
            placeholder="Write a comment…"
            value={text}
            onChange={e => setText(e.target.value)}
            maxLength={2000}
            rows={2}
            style={{
              display: 'block',
              width: '100%',
              padding: '8px 12px',
              background: 'transparent',
              color: 'inherit',
              border: 'none',
              outline: 'none',
              resize: 'vertical',
              fontFamily: 'inherit',
              fontSize: '0.875rem',
              lineHeight: '1.5',
              boxSizing: 'border-box',
              minHeight: '64px',
              maxHeight: '240px',
            }}
          />
        </Box>
        <Button
          type="submit"
          variant="contained"
          size="small"
          disabled={submitting || !text.trim()}
          sx={{ alignSelf: 'flex-end' }}
        >
          {submitting ? 'Posting…' : 'Post comment'}
        </Button>
      </Box>
    </Box>
  );
}

export default function BlogPostPage() {
  const { postId } = useParams<{ postId: string }>();
  const { authUser, currentUser } = useAuth();

  const [post, setPost] = React.useState<Post | null>(null);
  const [postLoading, setPostLoading] = React.useState(true);
  const [postError, setPostError] = React.useState('');

  const [comments, setComments] = React.useState<Comment[]>([]);
  const [deletingCommentId, setDeletingCommentId] = React.useState<string | null>(null);
  const [snack, setSnack] = React.useState<SnackState>({ open: false, message: '', severity: 'success' });

  const isAdmin = currentUser?.role === 'admin';
  const canPost = isAdmin || currentUser?.role === 'author';

  const { project } = usePostProject(postId);

  // Fetch the post document once
  React.useEffect(() => {
    if (!postId) return;
    let cancelled = false;

    async function loadPost() {
      setPostLoading(true);
      setPostError('');
      try {
        const snap = await getDoc(doc(db, 'posts', postId!));
        if (cancelled) return;
        if (!snap.exists()) {
          setPostError('Post not found.');
          return;
        }
        const data = snap.data();
        if (!data.published && !isAdmin && currentUser?.role !== 'author') {
          setPostError('Post not found.');
          return;
        }
        setPost({
          title: data.title,
          content: data.content,
          authorId: data.authorId,
          authorName: data.authorName,
          authorPhotoURL: data.authorPhotoURL,
          createdAt: data.createdAt ?? null,
          published: data.published,
        });
      } catch {
        if (!cancelled) setPostError('Failed to load post. Please refresh.');
      } finally {
        if (!cancelled) setPostLoading(false);
      }
    }

    loadPost();
    return () => { cancelled = true; };
  }, [postId, isAdmin, currentUser?.role]);

  // Subscribe to comments in real-time via onSnapshot
  React.useEffect(() => {
    if (!postId) return;
    const q = query(
      collection(db, 'posts', postId, 'comments'),
      orderBy('createdAt', 'asc')
    );
    const unsubscribe = onSnapshot(q, snapshot => {
      setComments(
        snapshot.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            content: data.content,
            authorId: data.authorId,
            authorName: data.authorName,
            authorPhotoURL: data.authorPhotoURL,
            createdAt: data.createdAt ?? null,
          };
        })
      );
    });
    return unsubscribe;
  }, [postId]);

  const handleDeleteComment = async (commentId: string) => {
    if (!postId) return;
    setDeletingCommentId(commentId);
    try {
      await deleteDoc(doc(db, 'posts', postId, 'comments', commentId));
    } catch {
      setSnack({ open: true, message: 'Failed to delete comment.', severity: 'error' });
    } finally {
      setDeletingCommentId(null);
    }
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 4 }, py: { xs: 2, sm: 4 }, maxWidth: 800, mx: 'auto' }}>
      {/* Back link */}
      <Button
        component={RouterLink}
        to="/blog"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 3 }}
        color="inherit"
      >
        All posts
      </Button>

      {postError ? (
        <Alert severity="error">{postError}</Alert>
      ) : postLoading ? (
        <PostSkeleton />
      ) : post ? (
        <>
          {/* Post header */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2, flex: 1 }}>
                {post.title}
              </Typography>
              {/* Edit button for post author or admin */}
              {(isAdmin || authUser?.uid === post.authorId) && canPost && (
                <Tooltip title="Edit post">
                  <IconButton
                    component={RouterLink}
                    to={`/blog/${postId}/edit`}
                    aria-label="Edit post"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Box
              component={RouterLink}
              to={`/profile/${post.authorId}`}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2, textDecoration: 'none', color: 'inherit' }}
            >
              <Avatar src={post.authorPhotoURL} sx={{ width: 36, height: 36, fontSize: '0.875rem' }}>
                {post.authorName[0]?.toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{post.authorName}</Typography>
                {post.createdAt && (
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(post.createdAt)}
                  </Typography>
                )}
              </Box>
            </Box>

            {!post.published && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This post is unpublished and only visible to authors and admins.
              </Alert>
            )}
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Post body — render markdown */}
          <Box
            sx={{
              '& h1,h2,h3,h4,h5,h6': { mt: 3, mb: 1, fontWeight: 700 },
              '& p': { mb: 1.5, lineHeight: 1.75 },
              '& pre': { bgcolor: 'action.hover', p: 2, borderRadius: 1, overflowX: 'auto' },
              '& code': { fontFamily: 'monospace', fontSize: '0.9em' },
              '& ul,ol': { pl: 3, mb: 1.5 },
              '& blockquote': { borderLeft: 4, borderColor: 'divider', pl: 2, ml: 0, color: 'text.secondary' },
              '& a': { color: 'primary.main' },
              '& img': { maxWidth: '100%', borderRadius: 1 },
            }}
          >
            <ReactMarkdown urlTransform={u => u.startsWith('data:image/') ? u : defaultUrlTransform(u)}>{post.content}</ReactMarkdown>
          </Box>

          <Divider sx={{ mt: 4, mb: 3 }} />

          {/* Related project callout */}
          {project && (
            <Paper
              variant="outlined"
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 2.5, py: 2, mb: 3, flexWrap: 'wrap' }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Related project
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {project.title}
                </Typography>
                {project.description && (
                  <Typography variant="body2" color="text.secondary">
                    {project.description}
                  </Typography>
                )}
              </Box>
              <Button
                component={RouterLink}
                to={project.url ?? `/projects/${project.slug}/`}
                endIcon={<LaunchIcon fontSize="small" />}
                variant="outlined"
                size="small"
                sx={{ flexShrink: 0 }}
              >
                Open project
              </Button>
            </Paper>
          )}

          {/* Comments */}
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            {comments.length === 0
              ? 'No comments yet'
              : `${comments.length} comment${comments.length === 1 ? '' : 's'}`}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {comments.map(c => {
              const canDelete = isAdmin || authUser?.uid === c.authorId;
              const isDeleting = deletingCommentId === c.id;
              return (
                <Box key={c.id} sx={{ display: 'flex', gap: 1.5 }}>
                  <Avatar src={c.authorPhotoURL} sx={{ width: 32, height: 32, fontSize: '0.75rem', mt: 0.25 }}>
                    {c.authorName[0]?.toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600 }}
                        component={RouterLink}
                        to={`/profile/${c.authorId}`}
                        style={{ color: 'inherit' }}
                      >{c.authorName}</Typography>
                      {c.createdAt && (
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(c.createdAt)}
                        </Typography>
                      )}
                      {canDelete && (
                        <Tooltip title="Delete comment">
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Delete comment"
                              disabled={isDeleting}
                              onClick={() => handleDeleteComment(c.id)}
                              sx={{ ml: 'auto' }}
                            >
                              {isDeleting ? <CircularProgress size={14} /> : <DeleteIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}>
                      {c.content}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Add comment form */}
          {authUser && currentUser ? (
            <CommentForm
              postId={postId!}
              photoURL={currentUser.photoURL}
              name={currentUser.name}
              onError={msg => setSnack({ open: true, message: msg, severity: 'error' })}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              <RouterLink to="/SignIn" style={{ color: 'inherit' }}>Sign in</RouterLink> to leave a comment.
            </Typography>
          )}
        </>
      ) : null}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
